// Vanuatu National Syllabus: Biology Senior Secondary Years 11-13
// Based on official syllabus document revised February 2023

export const biologyCurriculum = {
  year11: {
    title: "Year 11 Biology",
    description: "Foundation biological concepts covering cellular biology, genetics, organism-level biology, environmental biology, and variation and evolution",
    hoursPerYear: 132,
    hoursPerWeek: 4,
    assessment: {
      internalExamination: 60,
      continuousAssessment: 40
    },
    strands: [
      {
        id: "strand1",
        name: "Cellular Biology",
        hours: 30,
        percentage: 23,
        description: "Understanding the physiological properties of cell structure, organelles, and their interactions with the environment",
        topics: [
          {
            id: "11BIO1.1",
            name: "Cell Structure",
            description: "Plant and animal cell organelles and their structures",
            weeks: "1-4",
            learningOutcomes: [
              "Identify specialized cells in animals: nerve cells, muscle cells, red blood cells, sperm cells, and egg cells",
              "Identify specialized cells in plants: guard cells, phloem cells, and xylem cells",
              "Draw and label cell organelles of plant and animal cells",
              "Describe the features and functions of cell organelles",
              "Describe the structure and function of the cell membrane",
              "Calculate cell size using microscope measurements",
              "Explain the importance of biological organization",
              "Prepare wet mounts and list advantages and disadvantages"
            ]
          },
          {
            id: "11BIO1.2",
            name: "Cell Transport",
            description: "Diffusion, facilitated diffusion and osmosis in terms of energy requirement",
            weeks: "5-8",
            learningOutcomes: [
              "Define passive and active transport, concentration gradient, diffusion, facilitated diffusion, osmosis",
              "Define cytosis, endocytosis, pinocytosis, phagocytosis, and exocytosis",
              "Differentiate between passive and active transport in terms of energy requirements",
              "Relate transport types to concentration gradient",
              "Calculate volume ratio to surface area in cells",
              "Describe effects of osmosis in plant and animal cells",
              "Explain the relationship between solute concentrations (hypotonic, isotonic, hypertonic)"
            ]
          },
          {
            id: "11BIO1.3",
            name: "Enzymes",
            description: "The role and function of enzymes as biological catalysts",
            weeks: "9-11",
            learningOutcomes: [
              "Define biological catalyst, enzyme, and substrate",
              "Describe the biological function and role of enzymes",
              "Explain the induced fit model",
              "Describe enzyme specificity: substrate specificity and specificity of action",
              "Explain how environmental factors affect enzyme functioning (temperature, pH, substrate concentration, co-factors, inhibitors)",
              "Analyse graphs showing enzyme activity affected by environmental factors"
            ]
          }
        ]
      },
      {
        id: "strand2",
        name: "Genetics",
        hours: 24,
        percentage: 18,
        description: "Understanding molecular structure and function of genes, heredity and variation in living organisms",
        topics: [
          {
            id: "11BIO2.1",
            name: "Molecular Genetics",
            description: "Functions of the nucleus components and protein synthesis processes",
            weeks: "12-15",
            learningOutcomes: [
              "Define molecular genetics, nucleus, chromosomes, DNA, RNA, genes, alleles, proteins",
              "Identify main components of the nucleus and chromosomes",
              "Describe the structure of DNA and RNA (sugar, phosphate, nucleotide strands, bases, base pairing)",
              "Explain the relationship between chromosomes, DNA, RNA, genes, alleles and proteins",
              "Define amino acids, transcription, and translation",
              "Describe how amino acids are determined by DNA base sequence",
              "Explain the interaction between DNA, mRNA, tRNA in protein production",
              "Differentiate between transcription and translation"
            ]
          },
          {
            id: "11BIO2.2",
            name: "Cell Growth and Division",
            description: "Sequence of events in mitosis and meiosis",
            weeks: "16-18",
            learningOutcomes: [
              "Describe the process of DNA replication",
              "Explain the importance of DNA replication",
              "Identify and describe the phases of mitosis and meiosis",
              "Differentiate between mitosis and meiosis",
              "Relate cell divisions to daughter cells produced (chromosome number, variation, function)",
              "Explain how crossing over, recombination and independent assortment produces variation",
              "Discuss the role of mitosis and meiosis in organism life cycles"
            ]
          }
        ]
      },
      {
        id: "strand3",
        name: "Organism Level Biology",
        hours: 36,
        percentage: 27,
        description: "Understanding organization among plants and animals, structure and function in vital processes",
        topics: [
          {
            id: "11BIO3.1",
            name: "Reproduction in Plants",
            description: "Sexual and asexual reproduction in plants and role of cell division",
            weeks: "19-21",
            learningOutcomes: [
              "Define sexual and asexual reproduction in plants",
              "Describe both types of reproduction with advantages and disadvantages",
              "Explain fertilization, seed and spore dispersal in mosses, ferns, gymnosperms, angiosperms",
              "Describe roles of mitosis and meiosis in plant reproduction",
              "Describe alternation of generations in plant life cycles",
              "Define and compare sporophyte and gametophyte generations"
            ]
          },
          {
            id: "11BIO3.2",
            name: "Human Nutrition",
            description: "Modern and traditional Pacific diet and effects on human body",
            weeks: "22-24",
            learningOutcomes: [
              "Identify types of food in modern and traditional Pacific diets",
              "List advantages and disadvantages of traditional vs modern diet",
              "Describe diseases caused by modern diet (diabetes, NCDs)",
              "Identify features of a healthy digestive system",
              "Define and explain gastric ulcers and gall stones",
              "Explain the function of the liver and effects of high blood sugar",
              "Discuss implications of NCDs and ways to minimize risk"
            ]
          },
          {
            id: "11BIO3.3",
            name: "Animal Digestion",
            description: "How food is processed through the digestive tract of animals",
            weeks: "25-27",
            learningOutcomes: [
              "Define ingestion, digestion, absorption, and egestion",
              "Describe the processes and functions of each digestive organ",
              "List digestive enzymes in carnivores, herbivores, and omnivores",
              "Distinguish between digestive systems of different animal types",
              "Compare digestive systems of sea anemone, earthworm, and humans"
            ]
          },
          {
            id: "11BIO3.4",
            name: "Animal Gas Exchange",
            description: "Processes of breathing, gas exchange, and cellular respiration",
            weeks: "28-30",
            learningOutcomes: [
              "Define ventilation, gas exchange, and cellular respiration",
              "List gas exchange organs in humans",
              "Identify body parts associated with gas exchange in different animals",
              "Discuss how gas exchange systems depend on size and mobility",
              "Compare breathing, gas exchange and cellular respiration",
              "Explain gas exchange over different surfaces (gills, trachea, lung, body surface)"
            ]
          }
        ]
      },
      {
        id: "strand4",
        name: "Environmental Biology",
        hours: 30,
        percentage: 23,
        description: "Understanding how ecosystems are affected by human activities and organism adaptations",
        topics: [
          {
            id: "11BIO4.1",
            name: "Ecosystems",
            description: "Characteristics of ecosystems and relationship between biodiversity and survival",
            weeks: "31-34",
            learningOutcomes: [
              "Define population, community, biodiversity, ecosystem",
              "Distinguish between population, community and ecosystem",
              "Define and describe biotic and abiotic factors",
              "Define and draw food chains and food webs",
              "Explain energy flow through trophic levels",
              "Discuss human impacts on local ecosystems",
              "Explain relationship between biodiversity and ecosystem survival",
              "Evaluate loss of biodiversity and effects on ecosystem"
            ]
          },
          {
            id: "11BIO4.2",
            name: "Diversity of Organisms",
            description: "Classification levels and how diversity can be classified into groups",
            weeks: "35-36",
            learningOutcomes: [
              "Define biodiversity and classification",
              "Distinguish between common and scientific names",
              "Define classification levels: Kingdom, Phylum, Class, Order, Genus, Species",
              "Classify organisms according to similarity using scientific names",
              "Explain importance of each classification level"
            ]
          },
          {
            id: "11BIO4.3",
            name: "Adaptive Features",
            description: "Adaptation as a characteristic enabling organisms to survive better",
            weeks: "37-38",
            learningOutcomes: [
              "Identify and list characteristics that help organisms survive",
              "Describe structural, physiological, behavioural, and life history adaptations",
              "Define tolerance, optimum range, ecological niche, Gause's principle, Liebig's law",
              "Explain how adaptations relate to ecological concepts"
            ]
          }
        ]
      },
      {
        id: "strand5",
        name: "Variation and Evolution",
        hours: 12,
        percentage: 9,
        description: "Understanding variation in organisms and hereditary characteristics leading to evolution",
        topics: [
          {
            id: "11BIO5.1",
            name: "Variation in Species",
            description: "Events leading to Darwin's theory of natural selection",
            weeks: "39-40",
            learningOutcomes: [
              "Define natural selection and artificial selection",
              "Describe Darwin's theory of natural selection",
              "List evidence supporting Darwin's theory",
              "Describe features of natural and artificial selection",
              "Compare and contrast the two types of selection",
              "Define adaptive radiation and discuss causes",
              "Evaluate Darwin's evidence for natural selection"
            ]
          }
        ]
      }
    ]
  },
  year12: {
    title: "Year 12 Biology",
    description: "Advanced biological concepts covering cellular processes, Mendelian inheritance, organism systems, populations and evolution patterns",
    hoursPerYear: 132,
    hoursPerWeek: 4,
    assessment: {
      externalExamination: 60,
      internalAssessment: 40
    },
    strands: [
      {
        id: "strand1",
        name: "Cellular Biology",
        hours: 30,
        percentage: 23,
        description: "Understanding processes, mechanisms and significance of respiration, photosynthesis and cellular immunity",
        topics: [
          {
            id: "12BIO1.1",
            name: "Respiration",
            description: "Role of respiration in ATP production in aerobic and anaerobic respiration",
            weeks: "1-4",
            learningOutcomes: [
              "Define respiration and identify where it occurs in cells",
              "Describe the structure of mitochondria",
              "List raw materials and products of respiration",
              "Identify three phases: glycolysis, Krebs cycle, electron transport chain",
              "Describe the main activity of each respiration phase",
              "Compare products of aerobic and anaerobic respiration",
              "Discuss effect of substrate and temperature on reaction rate"
            ]
          },
          {
            id: "12BIO1.2",
            name: "Photosynthesis",
            description: "Process in the two phases of photosynthesis",
            weeks: "5-8",
            learningOutcomes: [
              "Define photosynthesis and limiting factor",
              "Explain importance of photosynthesis to life on earth",
              "Identify organelles where photosynthesis occurs",
              "Describe chloroplast structure and adaptations",
              "Explain light energy conversion in light-dependent reaction",
              "Outline key stages of photosynthesis",
              "Discuss effect of limiting factors on reaction rate",
              "Explain relationship between light and dark phases"
            ]
          },
          {
            id: "12BIO1.3",
            name: "Cellular Immunity",
            description: "Functions of antibodies in organism protection",
            weeks: "9-11",
            learningOutcomes: [
              "Describe properties of antibodies",
              "Explain how antibodies protect the organism",
              "Define phagocytosis and vaccination",
              "Describe roles of B lymphocytes, T lymphocytes, antigen-presenting cells, memory cells",
              "Describe modes of action of antibiotics like penicillin"
            ]
          },
          {
            id: "12BIO1.4",
            name: "Practical Skills",
            description: "Reporting on practical activities investigating biological principles",
            weeks: "Throughout",
            learningOutcomes: [
              "State aim and hypothesis for practical investigations",
              "Outline procedure and list equipment",
              "Draw diagrams of experimental setup",
              "Present results in tables and graphs",
              "Explain results and draw conclusions",
              "Evaluate results and make recommendations"
            ]
          }
        ]
      },
      {
        id: "strand2",
        name: "Genetics",
        hours: 24,
        percentage: 18,
        description: "Understanding molecular structure and function of genes, heredity and variation",
        topics: [
          {
            id: "12BIO2.1",
            name: "Mendelian Inheritance",
            description: "Mendel's experiments, results and conclusions",
            weeks: "12-15",
            learningOutcomes: [
              "Describe Mendel's crosses and results",
              "Explain how genotype links to phenotype",
              "Define key terms: segregation, independent assortment, crossing over, dominance types",
              "Define dominant/recessive alleles, heterozygous, homozygous, multiple alleles",
              "Describe complete dominance, incomplete dominance, codominance",
              "Distinguish between monohybrid and dihybrid crosses",
              "Evaluate Punnett squares to determine offspring characteristics"
            ]
          },
          {
            id: "12BIO2.2",
            name: "Diversity and Relatedness",
            description: "Anatomical similarities and genetic control during embryonic development",
            weeks: "16-18",
            learningOutcomes: [
              "Describe anatomical similarities in vertebrates",
              "Compare vertebrate positions (e.g., spinal cord in mouse vs frog)",
              "Describe stages of embryonic development in vertebrates",
              "Define and explain homeotic genes",
              "Explain control of homeotic genes on major body plans",
              "List and describe different levels of organization leading to common origin"
            ]
          }
        ]
      },
      {
        id: "strand3",
        name: "Organism Level Biology",
        hours: 36,
        percentage: 27,
        description: "Understanding organization among plants and animals, structure and function in vital processes",
        topics: [
          {
            id: "12BIO3.1",
            name: "Plant Form and Function",
            description: "Diversity in leaf, stem and root structure and function",
            weeks: "19-21",
            learningOutcomes: [
              "Compare leaf structure features including vascular tissue organization",
              "Compare stem structure including vascular tissue and pith",
              "Describe root structure features (stele, pith, cortex, endodermis)",
              "Relate plant features to environmental adaptation",
              "Explain functions of leaf, stem and root in various plant phyla"
            ]
          },
          {
            id: "12BIO3.2",
            name: "Animal Circulatory Systems",
            description: "Why animals above certain size require internal transport systems",
            weeks: "22-24",
            learningOutcomes: [
              "Discuss efficiency of closed single, closed double and open circulatory systems",
              "Explain circulatory system efficiency in relation to size and mobility",
              "Identify organisms with 2, 3 and 4 chambered hearts",
              "Define and differentiate open/closed and single/double circulatory systems",
              "Discuss reasons animals above certain size need internal transport"
            ]
          },
          {
            id: "12BIO3.3",
            name: "Excretion in Animals",
            description: "Production of carbon dioxide, water and nitrogenous wastes",
            weeks: "25-27",
            learningOutcomes: [
              "List main excretory organs and products in fish, insects and humans",
              "Describe structure and function of excretory organs",
              "Explain processes producing CO2, water and nitrogenous wastes",
              "Discuss excretory system efficiency in relation to environment"
            ]
          },
          {
            id: "12BIO3.4",
            name: "Nervous System",
            description: "Structure and function of the nervous system",
            weeks: "28-30",
            learningOutcomes: [
              "Describe neuron structure and characteristics",
              "Explain mechanism of nervous signal production and transmission",
              "Define and differentiate action potential and resting potential",
              "Differentiate actions of neurotransmitters",
              "Discuss how nervous, excretory and circulatory systems maintain body environment"
            ]
          }
        ]
      },
      {
        id: "strand4",
        name: "Environmental Biology",
        hours: 30,
        percentage: 23,
        description: "Understanding how ecosystems are affected by human activities and organism classifications",
        topics: [
          {
            id: "12BIO4.1",
            name: "Communities",
            description: "Characteristics of communities (patterns and stability) and pioneer species",
            weeks: "31-34",
            learningOutcomes: [
              "Define community and describe its characteristics",
              "List and differentiate zonation and stratification patterns",
              "Explain how succession and climax impact community stability",
              "Explain how competition affects community relationships",
              "Distinguish between predator-prey, parasitism, mutualism, commensalism",
              "Define and describe role of pioneer species"
            ]
          },
          {
            id: "12BIO4.2",
            name: "Population",
            description: "Population characteristics (size and space) and genetic variation advantage",
            weeks: "35-38",
            learningOutcomes: [
              "Define population, natality, mortality, territory, home range, distribution, density",
              "Define carrying capacity",
              "Identify and sketch survivorship curves",
              "Relate survivorship curves to natality and mortality",
              "Differentiate J-shaped and S-shaped growth curves",
              "Distinguish density dependent and independent factors",
              "Explain advantage of genetic variation for population survival",
              "Discuss effects of genetic isolation on populations"
            ]
          }
        ]
      },
      {
        id: "strand5",
        name: "Variation and Evolution",
        hours: 12,
        percentage: 9,
        description: "Understanding hereditary characteristics in successive generations leading to evolution",
        topics: [
          {
            id: "12BIO5.1",
            name: "Patterns of Evolution",
            description: "Divergent, adaptive radiation, convergent and parallel evolution",
            weeks: "39-40",
            learningOutcomes: [
              "Define divergent, convergent, and parallel evolution",
              "Describe conditions contributing to each evolution pattern",
              "Describe features of adaptive radiation using Darwin's finches",
              "Explain how parallel evolution occurs using wingless birds and insects"
            ]
          },
          {
            id: "12BIO5.2",
            name: "Speciation",
            description: "Allopatric and sympatric speciation and isolating mechanisms",
            weeks: "41-42",
            learningOutcomes: [
              "Define allopatric and sympatric speciation",
              "Define pre-zygotic and post-zygotic isolation",
              "Describe processes and results of isolation mechanisms",
              "Differentiate between speciation types with examples",
              "Discuss barriers causing speciation (ecological, reproductive)"
            ]
          }
        ]
      }
    ]
  },
  year13: {
    title: "Year 13 Biology",
    description: "Advanced concepts covering DNA replication, protein synthesis, genetics, organism behaviour, climate change and evolutionary mechanisms",
    hoursPerYear: 132,
    hoursPerWeek: 4,
    assessment: {
      externalExamination: 60,
      internalAssessment: 40,
      internalComponents: {
        task1: { name: "Genetics Investigation", weight: 10, strand: 2 },
        task2: { name: "Organism Level Biology Research", weight: 15, strand: 3 },
        task3: { name: "Environmental Biology Project", weight: 15, strand: 4 }
      }
    },
    strands: [
      {
        id: "strand1",
        name: "Cellular Biology",
        hours: 15,
        percentage: 10,
        description: "Understanding DNA replication and protein synthesis mechanisms",
        topics: [
          {
            id: "13BIO1.1",
            name: "DNA Replication",
            description: "Stages of replication and enzymes involved",
            weeks: "1-3",
            learningOutcomes: [
              "Define semi-conservative replication",
              "Describe importance of DNA replication",
              "Identify leading and lagging strands",
              "Identify enzymes involved (helicase, DNA polymerase, ligase)",
              "Describe direction of new strand synthesis (5' to 3')",
              "Explain roles of enzymes in DNA replication",
              "Explain process including Okazaki fragments",
              "Describe ways DNA replication problems may arise"
            ]
          },
          {
            id: "13BIO1.2",
            name: "Protein Synthesis",
            description: "Protein structure, functions and synthesis",
            weeks: "4-6",
            learningOutcomes: [
              "Define transcription, translation, codons, anticodons",
              "State functions of mRNA, tRNA, rRNA",
              "Describe features of structural and functional proteins",
              "Relate protein structure to function",
              "Describe processes of transcription and translation",
              "Describe use of genetic code to identify amino acids",
              "Describe features of genetic code (universal, non-overlapping, degenerate)",
              "Explain relationships within protein synthesis"
            ]
          }
        ]
      },
      {
        id: "strand2",
        name: "Genetics",
        hours: 28,
        percentage: 20,
        description: "Understanding gene concepts, metabolic pathways, linkages and biotechnology",
        topics: [
          {
            id: "13BIO2.1",
            name: "Gene Concept",
            description: "Gene structure, roles and influence on heredity",
            weeks: "7-9",
            learningOutcomes: [
              "Define genome and gene",
              "Describe structure and roles of genes",
              "Explain how genes function in variation and heredity",
              "Explain how genome determines organism characteristics",
              "Distinguish monohybrid from dihybrid crosses",
              "Evaluate Punnett squares for offspring characteristics"
            ]
          },
          {
            id: "13BIO2.2",
            name: "Metabolic Pathways and Linkage",
            description: "Metabolic pathways, genetic linkage and sex linkage",
            weeks: "10-12",
            learningOutcomes: [
              "Define metabolic pathway",
              "Describe phenylketonuria (PKU) characteristics",
              "Explain relation between metabolic pathway disorders and PKU",
              "Explain effects of mutation on enzyme control",
              "Define genetic linkage and sex linkage",
              "Describe inheritance of colour blindness and haemophilia",
              "Compare linked genes to sex-linked genes"
            ]
          },
          {
            id: "13BIO2.3",
            name: "Gene-Gene Interactions",
            description: "Mendelian inheritance and gene interactions",
            weeks: "13-15",
            learningOutcomes: [
              "Define complete dominance, incomplete dominance, codominance",
              "Define multiple alleles, epistasis, complementary genes, pleiotropy, polygenes",
              "Describe genotypes for various inheritance patterns",
              "Explain expression of characteristics from monohybrid crosses",
              "Explain gene-gene interactions (sickle cell disease, skin colour, height)",
              "Discuss epistasis using phenotypic ratios from dihybrid crosses"
            ]
          },
          {
            id: "13BIO2.4",
            name: "Biotechnology Application",
            description: "Gene cloning, transgenesis and DNA profiling",
            weeks: "16-18",
            learningOutcomes: [
              "Define gene cloning, transgenesis, bacterial plasmids",
              "Describe steps of using plasmids to produce gene copies",
              "Discuss recombinant DNA formation using restriction enzymes",
              "Define short tandem repeat (STR)",
              "Describe why each person's DNA is unique",
              "Define and describe PCR and gel electrophoresis",
              "Explain DNA profile formation and applications",
              "Discuss impact on criminal justice and medicine"
            ]
          },
          {
            id: "13BIO2.5",
            name: "Contemporary Biotechnology Issues",
            description: "Current biotechnology issues and their implications",
            weeks: "19-20",
            learningOutcomes: [
              "Name and locate contemporary biotechnology issues",
              "Describe human needs driving biotechnology applications",
              "Describe procedures used in biotechnology",
              "Compare differing opinions on biotechnology",
              "Discuss biological, social, ethical, economic impacts"
            ]
          }
        ]
      },
      {
        id: "strand3",
        name: "Organism Level Biology",
        hours: 36,
        percentage: 30,
        description: "Understanding ecological niches, animal behaviour and timing responses",
        topics: [
          {
            id: "13BIO3.1",
            name: "Ecological Niche",
            description: "Investigation of ecological niche of animal species",
            weeks: "21-23",
            learningOutcomes: [
              "Define ecological niche",
              "State aim and hypothesis for niche investigation",
              "Describe physical habitat and environment (abiotic/biotic factors)",
              "Describe adaptive features in relation to habitat",
              "Collect, record and process investigation data",
              "Explain findings and discuss biological significance"
            ]
          },
          {
            id: "13BIO3.2",
            name: "Orientation and Navigation",
            description: "Animal orientation and navigation processes",
            weeks: "24-26",
            learningOutcomes: [
              "Define innate and learned behaviour",
              "Distinguish between innate and learned behaviour",
              "Describe navigation methods (solar compass, star patterns, magnetic fields, chemical trails)",
              "Define homing and migration",
              "Explain adaptive value of migratory behaviour",
              "Define taxes and kinesis",
              "Describe adaptive value of taxes and kineses"
            ]
          },
          {
            id: "13BIO3.3",
            name: "Timing Responses",
            description: "Biological clocks and rhythms",
            weeks: "27-28",
            learningOutcomes: [
              "Define biological timing and biological clocks",
              "Define diurnal, nocturnal, crepuscular activity",
              "Define endogenous and exogenous biological rhythms",
              "Define actograms, free running period, phase shift, zeitgeber",
              "Explain activity diagrams of organisms",
              "Describe rhythmic cycles (daily, tidal, lunar, annual)",
              "Define circadian, circa tidal, circalunar, circannual rhythms"
            ]
          },
          {
            id: "13BIO3.4",
            name: "Interspecific Competition",
            description: "Interactions between species and survival in niches",
            weeks: "29-30",
            learningOutcomes: [
              "Define niche differentiation, out-competition, interspecific competition",
              "State competitive exclusion principle",
              "Describe features of competition",
              "Explain how competition limits population numbers",
              "Explain how competition leads to niche differentiation",
              "Describe and explain predator-prey relationships"
            ]
          },
          {
            id: "13BIO3.5",
            name: "Intraspecific Competition",
            description: "Interactions within species and survival",
            weeks: "31-32",
            learningOutcomes: [
              "Define r and k strategies, mating types, social organisations",
              "List advantages and disadvantages of group living",
              "Describe features of r and k strategies",
              "Explain how reproductive behaviour influences survival",
              "Describe social organisation in terms of hierarchies",
              "Define territory and home range",
              "Explain how territory establishment increases survival"
            ]
          }
        ]
      },
      {
        id: "strand4",
        name: "Environmental Biology",
        hours: 26,
        percentage: 20,
        description: "Understanding contemporary environmental issues including climate change and conservation",
        topics: [
          {
            id: "13BIO4.1",
            name: "Climate Change",
            description: "Challenges of contemporary environmental issues",
            weeks: "33-35",
            learningOutcomes: [
              "Define climate change and global warming",
              "State examples of greenhouse gases (CO2, methane, nitrous oxide)",
              "Describe human activities contributing to global warming",
              "List features of climate change (rising sea levels, extreme weather, coral bleaching)",
              "Explain how human activity contributes to climate change",
              "Explain how temperature changes impact plants and animals",
              "Discuss consequences on Pacific Island livelihoods"
            ]
          },
          {
            id: "13BIO4.2",
            name: "Conservation",
            description: "Adaptation and mitigation actions for climate change",
            weeks: "36-38",
            learningOutcomes: [
              "Define adaptation and mitigation in climate change context",
              "List conservation practices (afforestation, renewable energy)",
              "Identify and describe renewable energy sources",
              "List ways of adapting to climate change",
              "Explain adaptation strategies",
              "Construct models of renewable energy sources",
              "Discuss how renewable energy addresses climate change"
            ]
          }
        ]
      },
      {
        id: "strand5",
        name: "Variation and Evolution",
        hours: 27,
        percentage: 20,
        description: "Understanding variation, natural selection, gene pools and speciation",
        topics: [
          {
            id: "13BIO5.1",
            name: "Variation",
            description: "Types and causes of variation",
            weeks: "39-40",
            learningOutcomes: [
              "Define independent assortment, segregation, crossing over, recombinant genotypes",
              "Describe processes during meiosis that contribute to variation",
              "Describe mutation process leading to new alleles",
              "Explain contribution of mutation to gene pool",
              "Describe fertilization process",
              "Explain how fertilization produces variation",
              "Explain importance of variation in evolution"
            ]
          },
          {
            id: "13BIO5.2",
            name: "Natural Selection",
            description: "Natural selection and factors that influence it",
            weeks: "41-43",
            learningOutcomes: [
              "Define natural selection, selection pressure, sexual selection",
              "Outline main points of Darwin's theory",
              "List selecting agents for selection pressures",
              "Explain how biotic and abiotic factors act as forces of natural selection",
              "Describe features of sexual selection",
              "Define selective breeding and explain human role",
              "Define fitness and explain its contribution to allele frequency"
            ]
          },
          {
            id: "13BIO5.3",
            name: "Gene Pool and Allele Frequency",
            description: "Gene pools, allele frequencies and factors affecting them",
            weeks: "44-45",
            learningOutcomes: [
              "Define gene pool, allele frequency, founder effect, bottleneck effect",
              "List factors affecting allele frequency",
              "Explain how population size, natural selection and gene flow affect frequency",
              "Define genetic drift",
              "Explain how genetic drift affects allele frequency",
              "Describe founder effect and bottleneck effect as genetic drift cases"
            ]
          },
          {
            id: "13BIO5.4",
            name: "Speciation",
            description: "Types of speciation and reproductive isolating mechanisms",
            weeks: "46-47",
            learningOutcomes: [
              "Define allopatric and sympatric speciation",
              "Describe features and differences of speciation types",
              "List pre-mating and pre-zygotic reproductive isolating mechanisms",
              "Explain why different isolations lead to reproductive isolation",
              "Define hybrid inviable, hybrid sterile, hybrid breakdown",
              "Explain post-zygotic mechanisms leading to reproductive isolation"
            ]
          },
          {
            id: "13BIO5.5",
            name: "Patterns of Evolution",
            description: "Divergent, convergent and co-evolution",
            weeks: "48",
            learningOutcomes: [
              "Define divergent, convergent and co-evolution",
              "Define homologous and analogous structures",
              "Describe features of divergent evolution and homologous structures",
              "Describe features of convergent evolution and analogous structures",
              "Compare and contrast divergent and convergent evolution",
              "Describe features of co-evolution"
            ]
          }
        ]
      }
    ]
  }
};

export default biologyCurriculum;
